import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Button, Tooltip, Typography, Space, Checkbox, Popover, Row, Col, ConfigProvider } from "antd";
import { messageInstance } from "App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FolderMetadataSearchFilter } from "../Components/FolderMetadataSearchFilter";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { ModalCloseBtn } from "Components/General/Modal/ModalCloseBtn";
import { Button as ToolbarButton } from "Components/General/Button";
import { folderMetadataOverviewStore } from "../Stores/FolderMetadataOverviewStore";
import type {
  FolderMetadataOverviewEntry,
  FolderMetadataSortBy,
  FolderMetadataSortOrder,
} from "../Stores/FolderMetadataOverviewStore";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { projectsDataStore, userInfoStore } from "Stores";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps, ActiveFilters } from "Components/ScrollableTable/ScrollableTable";
import { SortDirectionType } from "react-virtualized";
import * as XLSX from "xlsx";
import { download } from "utils/download";
import { BrowserStorage } from "utils/BrowserStorage";

/** Row with synthetic keys for ScrollableTable (rowKey + optional column-only dataKeys). */
type FolderMetadataOverviewEntryWithKey = FolderMetadataOverviewEntry & {
  _rowKey: string;
  /** No API field; used as `dataKey` for the Source column. */
  source?: never;
};

const COLUMNS_STORAGE_KEY = "folderMetadataOverview_visibleColumns";
/** Persisted column pixel widths (ScrollableTable saves by dataKey). */
const COLUMN_WIDTHS_STORAGE_KEY = "folderMetadataOverview_columnWidths";
/** Active & Show columns are on by default so Yes/No filters are available without opening “Columns”. */
const DEFAULT_VISIBLE_COLUMNS = {
  active: true,
  show: true,
  type: false,
  required: false,
  format: false,
};
/** Default column order (dataKeys). Source and Set on folder are always shown. */
const DEFAULT_COLUMN_ORDER = [
  "source",
  "sourceFolderPath",
  "folderPath",
  "metadataName",
  "definitionActive",
  "definitionShow",
  "definitionType",
  "definitionRequired",
  "definitionFormat",
  "defaultwert",
];

/**
 * Direct vs inherited row. Backend defaults `direct` to true; missing property → treat as direct
 * (avoids falsy `undefined` showing everything as inherited).
 */
function isOverviewMetadataDirect(row: Pick<FolderMetadataOverviewEntry, "direct">): boolean {
  return row.direct !== false;
}

type DefinitionColumnSortKey =
  | "definitionActive"
  | "definitionShow"
  | "definitionType"
  | "definitionRequired"
  | "definitionFormat";

type SourceColumnFilterMode = "all" | "direct" | "inherited";
type BoolTriColumnFilter = "all" | "yes" | "no";

function metadataTypeValue(d: API_Metadata): number | undefined {
  return d.type ?? (d as { typ?: number }).typ;
}

/** Shared label text for definition columns (sort, Excel, cells). Requires definitionsfull lookup. */
function getDefinitionColumnText(
  d: API_Metadata | undefined,
  key: DefinitionColumnSortKey,
  t: (s: string) => string
): string {
  if (!d) return "—";
  switch (key) {
    case "definitionActive": {
      if (d.active === true) return t("Yes");
      if (d.active === false) return t("No");
      return "—";
    }
    case "definitionShow": {
      if (d.show === true) return t("Yes");
      if (d.show === false) return t("No");
      return "—";
    }
    case "definitionType":
      return metadataTypeValue(d) === 1 ? t("From filename") : t("Manual");
    case "definitionRequired":
      return d.required === true ? t("Yes") : t("No");
    case "definitionFormat":
      return d.format ?? "—";
    default:
      return "—";
  }
}

function rowPassesOverviewColumnFilters(
  row: FolderMetadataOverviewEntry,
  sourceColumnFilter: SourceColumnFilterMode,
  activeColumnFilter: BoolTriColumnFilter,
  showColumnFilter: BoolTriColumnFilter
): boolean {
  if (sourceColumnFilter === "direct" && !isOverviewMetadataDirect(row)) return false;
  if (sourceColumnFilter === "inherited" && isOverviewMetadataDirect(row)) return false;
  const def = row.definition;
  if (activeColumnFilter === "yes" && def?.active !== true) return false;
  if (activeColumnFilter === "no" && def?.active !== false) return false;
  if (showColumnFilter === "yes" && def?.show !== true) return false;
  if (showColumnFilter === "no" && def?.show !== false) return false;
  return true;
}

function excelCellForOverviewColumn(
  key: string,
  row: FolderMetadataOverviewEntry,
  def: API_Metadata | undefined,
  t: (s: string) => string
): string {
  switch (key) {
    case "folderPath":
      return row.folderPath ?? "";
    case "metadataName":
      return row.metadataName ?? "";
    case "defaultwert":
      return row.defaultwert ?? "";
    case "source":
      return isOverviewMetadataDirect(row) ? t("Direct") : t("Inherited");
    case "sourceFolderPath":
      return isOverviewMetadataDirect(row) ? "—" : row.sourceFolderPath ?? "";
    case "definitionActive":
    case "definitionShow":
    case "definitionType":
    case "definitionRequired":
    case "definitionFormat":
      return getDefinitionColumnText(def, key as DefinitionColumnSortKey, t);
    default:
      return "";
  }
}

export const FolderMetadataOverviewPage: React.FC<ModalComponentProps> = observer(({ hideModal }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const projectId = projectsDataStore.projectId;

  const definitionForRow = useCallback(
    (row: FolderMetadataOverviewEntry): API_Metadata | undefined =>
      row.definition ?? folderMetadataOverviewStore.getDefinitionForEntry(row.metadataDefinitionId, row.metadataName),
    [folderMetadataOverviewStore.metadataDefinitions, folderMetadataOverviewStore.data]
  );

  const canAccessMetadataOverview = userInfoStore.isSysAdmin || projectsDataStore.isAdmin;

  useEffect(() => {
    if (!canAccessMetadataOverview) {
      hideModal?.();
    }
  }, [canAccessMetadataOverview, hideModal]);

  const tableRef = useRef<ScrollableTable<FolderMetadataOverviewEntryWithKey>>(null);
  /** Column filter modes (also drive includeInherited for “Source”). Empty = show all. */
  const [sourceColumnFilter, setSourceColumnFilter] = useState<SourceColumnFilterMode>("all");
  const [activeColumnFilter, setActiveColumnFilter] = useState<BoolTriColumnFilter>("all");
  const [showColumnFilter, setShowColumnFilter] = useState<BoolTriColumnFilter>("all");
  const loadSavedColumnVisibility = useCallback(() => {
    try {
      const parsed = BrowserStorage.storePermanent(COLUMNS_STORAGE_KEY) as
        | Partial<typeof DEFAULT_VISIBLE_COLUMNS>
        | undefined;
      if (parsed && typeof parsed === "object") {
        return { ...DEFAULT_VISIBLE_COLUMNS, ...parsed };
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_VISIBLE_COLUMNS;
  }, []);

  /**
   * Toggle which definition columns are visible (Active, Show, Type, Required, Format).
   * Persisted via BrowserStorage.
   */
  const [visibleDefinitionColumns, setVisibleDefinitionColumns] = useState(() => loadSavedColumnVisibility());

  const persistColumnVisibility = useCallback((next: typeof visibleDefinitionColumns) => {
    try {
      BrowserStorage.storePermanent(COLUMNS_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const handleColumnVisibilityChange = useCallback(
    (key: keyof typeof DEFAULT_VISIBLE_COLUMNS, checked: boolean) => {
      const next = { ...visibleDefinitionColumns, [key]: checked };
      setVisibleDefinitionColumns(next);
      persistColumnVisibility(next);
    },
    [visibleDefinitionColumns, persistColumnVisibility]
  );

  const loadOverview = useCallback(async () => {
    try {
      await folderMetadataOverviewStore.fetchDefinitionsAndOverview();
    } catch {
      messageInstance.error(t("Failed to load folder metadata overview."));
    }
  }, [t]);

  const handleRefresh = useCallback(() => {
    loadOverview().catch(() => undefined);
  }, [loadOverview]);

  useEffect(() => {
    loadOverview().catch(() => undefined);
    return () => folderMetadataOverviewStore.reset();
  }, [projectId, loadOverview]);

  const goToFolder = useCallback(
    (folderId: number) => {
      documentsTableStore.goToFolder(folderId).catch(() => undefined);
      hideModal?.();
    },
    [hideModal]
  );

  const isDefinitionSortKey = useCallback(
    (key: string) =>
      ["definitionActive", "definitionShow", "definitionType", "definitionRequired", "definitionFormat"].includes(key),
    []
  );

  /** Non–definition sorts use API ordering (`fetch`). Definition columns sort client-side in `tableData` only. */
  const handleSort = useCallback(
    (sortBy: FolderMetadataSortBy, sortOrder: FolderMetadataSortOrder) => {
      folderMetadataOverviewStore.setSort(sortBy, sortOrder);
      if (!isDefinitionSortKey(sortBy)) {
        folderMetadataOverviewStore.fetch();
      }
    },
    [isDefinitionSortKey]
  );

  /**
   * Push folder/metadata/value filters to the store (refetch).
   * Source/Active/Show also set local state → `dataFiltered`.
   */
  const handleFilter = useCallback((filters: ActiveFilters) => {
    const search = (filters.folderPath as string[])?.[0] ?? "";
    const metadataNames = (filters.metadataName as string[]) ?? [];
    const metadataValue = (filters.defaultwert as string[])?.[0] ?? "";

    const sourceSel = ((filters.source as string[]) ?? [])[0];
    if (sourceSel === "direct") {
      setSourceColumnFilter("direct");
      folderMetadataOverviewStore.setIncludeInherited(false);
    } else if (sourceSel === "inherited") {
      setSourceColumnFilter("inherited");
      folderMetadataOverviewStore.setIncludeInherited(true);
    } else {
      setSourceColumnFilter("all");
      folderMetadataOverviewStore.setIncludeInherited(true);
    }

    const activeSel = ((filters.definitionActive as string[]) ?? [])[0];
    if (activeSel === "yes") setActiveColumnFilter("yes");
    else if (activeSel === "no") setActiveColumnFilter("no");
    else setActiveColumnFilter("all");

    const showSel = ((filters.definitionShow as string[]) ?? [])[0];
    if (showSel === "yes") setShowColumnFilter("yes");
    else if (showSel === "no") setShowColumnFilter("no");
    else setShowColumnFilter("all");

    folderMetadataOverviewStore.setMultipleFieldSearch(search);
    folderMetadataOverviewStore.setFilters(metadataNames, metadataValue);
    folderMetadataOverviewStore.fetch();
  }, []);

  const handleResetFiltersAndSort = useCallback(() => {
    folderMetadataOverviewStore.setMultipleFieldSearch("");
    folderMetadataOverviewStore.setFilters([], "");
    folderMetadataOverviewStore.setSort("folderPath", "ASC");
    folderMetadataOverviewStore.setIncludeInherited(true);
    setSourceColumnFilter("all");
    setActiveColumnFilter("all");
    setShowColumnFilter("all");
    tableRef.current?.resetFilters();
    folderMetadataOverviewStore.fetch();
  }, []);

  useEffect(() => {
    const filters: ActiveFilters = {};
    if (folderMetadataOverviewStore.multipleFieldSearch)
      filters.folderPath = [folderMetadataOverviewStore.multipleFieldSearch];
    if (folderMetadataOverviewStore.metadataNames.length)
      filters.metadataName = folderMetadataOverviewStore.metadataNames;
    if (folderMetadataOverviewStore.metadataValue) filters.defaultwert = [folderMetadataOverviewStore.metadataValue];
    tableRef.current?.manageFilters(filters);
  }, []);

  /**
   * Rows already match server-side filters (search, metadata names, sort, includeInherited).
   * Source / Active / Show **column** filters are applied only in the UI (see handleFilter); they narrow
   * this list without a refetch so the table stays responsive.
   */
  const dataFiltered = useMemo(() => {
    const data = folderMetadataOverviewStore.data ?? [];
    return data.filter((row) =>
      rowPassesOverviewColumnFilters(row, sourceColumnFilter, activeColumnFilter, showColumnFilter)
    );
  }, [folderMetadataOverviewStore.data, sourceColumnFilter, activeColumnFilter, showColumnFilter]);

  const activeColumnFilterLabels = useMemo(() => {
    const labels: string[] = [];
    if (folderMetadataOverviewStore.multipleFieldSearch?.trim()) labels.push(t("Folder path"));
    if (folderMetadataOverviewStore.metadataNames.length > 0) labels.push(t("Metadata name"));
    if (folderMetadataOverviewStore.metadataValue?.trim()) labels.push(t("Value"));
    if (sourceColumnFilter !== "all") labels.push(t("Source"));
    if (activeColumnFilter !== "all") labels.push(t("Active"));
    if (showColumnFilter !== "all") labels.push(t("Show"));
    return labels;
  }, [
    folderMetadataOverviewStore.multipleFieldSearch,
    folderMetadataOverviewStore.metadataNames,
    folderMetadataOverviewStore.metadataValue,
    sourceColumnFilter,
    activeColumnFilter,
    showColumnFilter,
    t,
  ]);

  const stringCompare = (a: string, b: string) => ((a ?? "").toLowerCase() < (b ?? "").toLowerCase() ? -1 : 1);
  const sortDir = folderMetadataOverviewStore.sortOrder === "DESC" ? -1 : 1;

  /** Definition columns (Active, Show, Type, Required, Format) - only include when visible */
  const definitionColumns = useMemo((): TableColumnProps<FolderMetadataOverviewEntryWithKey>[] => {
    const cols: TableColumnProps<FolderMetadataOverviewEntryWithKey>[] = [];
    const getVal = (r: FolderMetadataOverviewEntryWithKey, key: string): string =>
      getDefinitionColumnText(definitionForRow(r), key as DefinitionColumnSortKey, t);
    const defSorter = (key: string) => (a: FolderMetadataOverviewEntryWithKey, b: FolderMetadataOverviewEntryWithKey) =>
      stringCompare(getVal(a, key), getVal(b, key));

    if (visibleDefinitionColumns.active) {
      cols.push({
        dataKey: "definitionActive" as keyof FolderMetadataOverviewEntryWithKey,
        label: (
          <Tooltip
            title={t("Whether this metadata is active in the project. Inactive metadata is hidden during normal use.")}
          >
            <span>{t("Active")}</span>
          </Tooltip>
        ),
        width: 80,
        minWidth: 56,
        flexShrink: 0,
        disableSort: false,
        filters: [
          { value: "yes", text: t("Yes") },
          { value: "no", text: t("No") },
        ],
        singleFilter: true,
        sorter: (a, b) => sortDir * defSorter("definitionActive")(a, b),
        cellRenderer: ({ rowData }) => getDefinitionColumnText(definitionForRow(rowData), "definitionActive", t),
      });
    }
    if (visibleDefinitionColumns.show) {
      cols.push({
        dataKey: "definitionShow" as keyof FolderMetadataOverviewEntryWithKey,
        label: (
          <Tooltip title={t("Whether this metadata is shown in dialogs and overviews for project users.")}>
            <span>{t("Show")}</span>
          </Tooltip>
        ),
        width: 80,
        minWidth: 56,
        flexShrink: 0,
        disableSort: false,
        filters: [
          { value: "yes", text: t("Yes") },
          { value: "no", text: t("No") },
        ],
        singleFilter: true,
        sorter: (a, b) => sortDir * defSorter("definitionShow")(a, b),
        cellRenderer: ({ rowData }) => getDefinitionColumnText(definitionForRow(rowData), "definitionShow", t),
      });
    }
    if (visibleDefinitionColumns.type) {
      cols.push({
        dataKey: "definitionType" as keyof FolderMetadataOverviewEntryWithKey,
        label: (
          <Tooltip title={t("Manual: entered by users. From filename: filled in automatically from the file name.")}>
            <span>{t("Type")}</span>
          </Tooltip>
        ),
        width: 90,
        minWidth: 72,
        flexShrink: 0,
        disableSort: false,
        sorter: (a, b) => sortDir * defSorter("definitionType")(a, b),
        cellRenderer: ({ rowData }) => getDefinitionColumnText(definitionForRow(rowData), "definitionType", t),
      });
    }
    if (visibleDefinitionColumns.required) {
      cols.push({
        dataKey: "definitionRequired" as keyof FolderMetadataOverviewEntryWithKey,
        label: (
          <Tooltip title={t("Whether a value must be entered for this metadata.")}>
            <span>{t("Required")}</span>
          </Tooltip>
        ),
        width: 85,
        minWidth: 56,
        flexShrink: 0,
        disableSort: false,
        sorter: (a, b) => sortDir * defSorter("definitionRequired")(a, b),
        cellRenderer: ({ rowData }) => getDefinitionColumnText(definitionForRow(rowData), "definitionRequired", t),
      });
    }
    if (visibleDefinitionColumns.format) {
      cols.push({
        dataKey: "definitionFormat" as keyof FolderMetadataOverviewEntryWithKey,
        label: (
          <Tooltip title={t("How the metadata value must be entered (e.g. free text, selection list, or date).")}>
            <span>{t("Format")}</span>
          </Tooltip>
        ),
        width: 80,
        minWidth: 64,
        flexShrink: 0,
        disableSort: false,
        sorter: (a, b) => sortDir * defSorter("definitionFormat")(a, b),
        cellRenderer: ({ rowData }) => {
          const d = definitionForRow(rowData);
          if (d == null) return "—";
          const fmt = d.format ?? "—";
          const hasWertebereich = Boolean(fmt?.toLowerCase().includes("w")) && (d.rangeList?.length ?? 0) > 0;
          if (hasWertebereich) {
            const wertebereichText = (d.rangeList ?? [])
              .map((item) => (item.description ? `${item.value} - ${item.description}` : item.value))
              .join("\n");
            return (
              <Tooltip title={wertebereichText}>
                <span>{fmt}</span>
              </Tooltip>
            );
          }
          return fmt;
        },
      });
    }
    return cols;
  }, [visibleDefinitionColumns, t, sortDir, definitionForRow]);

  const columnKeyToLabel: Record<string, string> = useMemo(
    () => ({
      folderPath: t("Folder path"),
      metadataName: t("Metadata name"),
      definitionActive: t("Active"),
      definitionShow: t("Show"),
      definitionType: t("Type"),
      definitionRequired: t("Required"),
      definitionFormat: t("Format"),
      defaultwert: t("Value"),
      source: t("Source"),
      sourceFolderPath: t("Set on folder"),
    }),
    [t]
  );

  const currentColumnKeys = useMemo(() => {
    const keys: string[] = [
      "folderPath",
      "metadataName",
      ...(visibleDefinitionColumns.active ? ["definitionActive"] : []),
      ...(visibleDefinitionColumns.show ? ["definitionShow"] : []),
      ...(visibleDefinitionColumns.type ? ["definitionType"] : []),
      ...(visibleDefinitionColumns.required ? ["definitionRequired"] : []),
      ...(visibleDefinitionColumns.format ? ["definitionFormat"] : []),
      "defaultwert",
      "source",
      "sourceFolderPath",
    ];
    return keys;
  }, [visibleDefinitionColumns]);

  /**
   * Merge default column order with whatever columns exist right now (visibility toggles add/remove keys).
   * 1) Walk default order and keep keys that still apply. 2) Append any new keys at the end.
   */
  const orderedColumnKeys = useMemo(() => {
    const orderSet = new Set(DEFAULT_COLUMN_ORDER);
    const ordered: string[] = [];
    for (const key of DEFAULT_COLUMN_ORDER) {
      if (currentColumnKeys.includes(key)) ordered.push(key);
    }
    for (const key of currentColumnKeys) {
      if (orderSet.has(key)) {
        continue;
      }
      ordered.push(key);
    }
    return ordered;
  }, [currentColumnKeys]);

  const handleExportExcel = useCallback(() => {
    const rows = dataFiltered.map((r) => {
      const def = definitionForRow(r);
      const row: Record<string, string> = {};
      for (const key of orderedColumnKeys) {
        const label = columnKeyToLabel[key] ?? key;
        row[label] = excelCellForOverviewColumn(key, r, def, t);
      }
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("Folder metadata (Excel)"));
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    download(
      new Blob([buf]),
      t("Folder metadata overview export filename", { defaultValue: "folder-metadata-overview.xlsx" }),
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
    messageInstance.success(t("Export completed."));
  }, [dataFiltered, orderedColumnKeys, columnKeyToLabel, definitionForRow, t]);

  const columnsVisibilityContent = (
    <Space direction="vertical" size="small">
      <span className="filter-label">{t("Definition columns")}</span>
      <Checkbox
        checked={visibleDefinitionColumns.active}
        onChange={(e) => handleColumnVisibilityChange("active", e.target.checked)}
      >
        {t("Active")}
      </Checkbox>
      <Checkbox
        checked={visibleDefinitionColumns.show}
        onChange={(e) => handleColumnVisibilityChange("show", e.target.checked)}
      >
        {t("Show")}
      </Checkbox>
      <Checkbox
        checked={visibleDefinitionColumns.type}
        onChange={(e) => handleColumnVisibilityChange("type", e.target.checked)}
      >
        {t("Type")}
      </Checkbox>
      <Checkbox
        checked={visibleDefinitionColumns.required}
        onChange={(e) => handleColumnVisibilityChange("required", e.target.checked)}
      >
        {t("Required")}
      </Checkbox>
      <Checkbox
        checked={visibleDefinitionColumns.format}
        onChange={(e) => handleColumnVisibilityChange("format", e.target.checked)}
      >
        {t("Format")}
      </Checkbox>
    </Space>
  );

  /**
   * Column model: build a logical `base` array (folder → metadata → optional definition cols → value → source cols),
   * then apply fixed `DEFAULT_COLUMN_ORDER`. First pass: emit columns in default order; second pass: append any
   * column not listed (e.g. first show of a definition column) at the end.
   */
  const columns = useMemo((): TableColumnProps<FolderMetadataOverviewEntryWithKey>[] => {
    const headerWithTooltip = (labelKey: string, tooltipKey: string) => (
      <Tooltip title={t(tooltipKey)}>
        <span>{t(labelKey)}</span>
      </Tooltip>
    );

    const base: TableColumnProps<FolderMetadataOverviewEntryWithKey>[] = [
      {
        dataKey: "folderPath",
        headerDataTestId: "folder-metadata-overview-folder-path-column",
        filterIconDataTestId: "folder-metadata-overview-folder-path-filter",
        label: headerWithTooltip("Folder path", "Column help folder path"),
        width: 220,
        minWidth: 120,
        flexGrow: 1,
        filters: undefined,
        customFilterRender: (props) => (
          <FolderMetadataSearchFilter {...props} testIdSuffix="folderPath" placeholder={t("Folder path")} />
        ),
        sorter: (a, b) => sortDir * stringCompare(a.folderPath, b.folderPath),
        cellRenderer: ({ rowData }) => (
          <Tooltip title={rowData.folderPath}>
            <Typography.Link
              onClick={() => goToFolder(rowData.folderId)}
              style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {rowData.folderPath || "/"}
            </Typography.Link>
          </Tooltip>
        ),
      },
      {
        dataKey: "metadataName",
        label: headerWithTooltip("Metadata name", "Column help metadata name"),
        width: 160,
        minWidth: 88,
        flexShrink: 0,
        filters: folderMetadataOverviewStore.metadataFilterOptions,
        filterPredicate: (f, v) => f === v,
        sorter: (a, b) => sortDir * stringCompare(a.metadataName, b.metadataName),
        cellRenderer: ({ cellData }) => cellData ?? "",
      },
      ...definitionColumns,
      {
        dataKey: "defaultwert",
        headerDataTestId: "folder-metadata-overview-value-column",
        filterIconDataTestId: "folder-metadata-overview-value-filter",
        label: headerWithTooltip("Value", "Column help value"),
        width: 140,
        minWidth: 72,
        flexShrink: 0,
        customFilterRender: (props) => (
          <FolderMetadataSearchFilter {...props} testIdSuffix="defaultwert" placeholder={t("Filter by value")} />
        ),
        sorter: (a, b) => sortDir * stringCompare(a.defaultwert, b.defaultwert),
        cellRenderer: ({ cellData }) => cellData ?? "",
      },
    ];
    const isDirect = (r: FolderMetadataOverviewEntryWithKey) => isOverviewMetadataDirect(r);
    const originFolderPath = (r: FolderMetadataOverviewEntryWithKey) =>
      isDirect(r) ? r.folderPath : r.sourceFolderPath ?? r.folderPath;
    const originFolderId = (r: FolderMetadataOverviewEntryWithKey) =>
      isDirect(r) ? r.folderId : r.sourceFolderId ?? r.folderId;

    base.push(
      {
        dataKey: "source",
        headerDataTestId: "folder-metadata-overview-source-column",
        filterIconDataTestId: "folder-metadata-overview-source-column-filter",
        label: headerWithTooltip("Source", "Column help source"),
        width: 110,
        minWidth: 88,
        flexShrink: 0,
        filters: [
          { value: "direct", text: t("Direct only") },
          { value: "inherited", text: t("Inherited only") },
        ],
        singleFilter: true,
        disableSort: false,
        sorter: (a, b) =>
          sortDir * stringCompare(isDirect(a) ? "DIRECT" : "INHERITED", isDirect(b) ? "DIRECT" : "INHERITED"),
        cellRenderer: ({ rowData }) =>
          isDirect(rowData) ? (
            <span className="folder-metadata-overview__direct">{t("Direct")}</span>
          ) : (
            <Tooltip title={t("Inherited from folder: {{path}}", { path: rowData.sourceFolderPath || "/" })}>
              <span className="folder-metadata-overview__inherited">{t("Inherited")}</span>
            </Tooltip>
          ),
      },
      {
        dataKey: "sourceFolderPath",
        label: headerWithTooltip("Set on folder", "Column help set on folder"),
        width: 200,
        minWidth: 100,
        flexShrink: 0,
        disableSort: false,
        sorter: (a, b) => sortDir * stringCompare(originFolderPath(a), originFolderPath(b)),
        cellRenderer: ({ rowData }) =>
          isDirect(rowData) ? (
            <span style={{ color: "var(--text-color-secondary)" }}>—</span>
          ) : (
            <Typography.Link
              onClick={() => goToFolder(originFolderId(rowData))}
              style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {originFolderPath(rowData) || "/"}
            </Typography.Link>
          ),
      }
    );
    const byKey = new Map(base.map((c) => [c.dataKey as string, c]));
    const ordered: TableColumnProps<FolderMetadataOverviewEntryWithKey>[] = [];
    const seen = new Set<string>();
    for (const key of DEFAULT_COLUMN_ORDER) {
      const col = byKey.get(key);
      if (col) {
        ordered.push(col);
        seen.add(key);
      }
    }
    for (const col of base) {
      const k = col.dataKey as string;
      if (!seen.has(k)) ordered.push(col);
    }
    return ordered;
  }, [t, goToFolder, folderMetadataOverviewStore.metadataFilterOptions, definitionColumns, sortDir]);

  /**
   * Virtualized row list: attach stable `_rowKey` for ScrollableTable.
   * Sorting: folder/metadata/value/source columns are ordered by the **API** (`store.fetch` query). Definition
   * columns (Active, Show, …) are **not** in that API sort—values come from `metadataDefinitions`—so when the user
   * sorts by those we sort this array client-side to match the column sorter.
   */
  const tableData = useMemo((): FolderMetadataOverviewEntryWithKey[] => {
    const rows = dataFiltered.map((r) => ({
      ...r,
      _rowKey: `${r.folderId}-${r.metadataDefinitionId}-${r.sourceFolderId ?? r.folderId}-${
        isOverviewMetadataDirect(r) ? "d" : "i"
      }`,
    }));
    const sortBy = folderMetadataOverviewStore.sortBy;
    if (!isDefinitionSortKey(sortBy)) return rows;
    const cmp = (a: FolderMetadataOverviewEntryWithKey, b: FolderMetadataOverviewEntryWithKey) =>
      stringCompare(
        getDefinitionColumnText(definitionForRow(a), sortBy as DefinitionColumnSortKey, t),
        getDefinitionColumnText(definitionForRow(b), sortBy as DefinitionColumnSortKey, t)
      );
    return [...rows].sort((a, b) => sortDir * cmp(a, b));
  }, [
    dataFiltered,
    folderMetadataOverviewStore.sortBy,
    folderMetadataOverviewStore.sortOrder,
    isDefinitionSortKey,
    definitionForRow,
    t,
    stringCompare,
    sortDir,
  ]);

  if (!canAccessMetadataOverview) {
    return null;
  }

  return (
    <ConfigProvider componentDisabled={false}>
      <div
        className="folder-metadata-overview-page"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
        data-testid="folder-metadata-overview-page"
      >
        <div style={{ paddingRight: 40 }}>
          <div className="viewer-modal-title">{t("Folder metadata overview")}</div>
          <Row style={{ minHeight: 45 }} gutter={10} justify="space-between" align="top">
            <Col flex="1 1 auto" style={{ marginBottom: 8 }}>
              <Space wrap size="middle" align="center">
                <Tooltip title={t("Clear all filters and reset sorting to default.")}>
                  <Button
                    data-testid="folder-metadata-overview-reset-filters"
                    icon={<FontAwesomeIcon icon={["far", "undo"]} />}
                    onClick={handleResetFiltersAndSort}
                  >
                    {t("Reset filters")}
                  </Button>
                </Tooltip>
                {activeColumnFilterLabels.length > 0 ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, maxWidth: 480 }}
                    ellipsis={{ tooltip: true }}
                    data-testid="folder-metadata-overview-active-column-filters"
                  >
                    {t("Column filters active hint", {
                      list: activeColumnFilterLabels.join(", "),
                      defaultValue: "Column filters: {{list}}",
                    })}
                  </Typography.Text>
                ) : null}
                <Popover content={columnsVisibilityContent} title={t("Visible columns")} trigger="click">
                  <Button data-testid="folder-metadata-overview-columns" icon={<FontAwesomeIcon icon="columns" />}>
                    {t("Columns")}
                  </Button>
                </Popover>
              </Space>
            </Col>
            <Col flex="none" style={{ marginBottom: 8 }}>
              <Space>
                <Tooltip title={t("Export current filtered data to Excel.")}>
                  <ToolbarButton
                    data-testid="folder-metadata-overview-export-excel"
                    faIcon={["far", "file-excel"]}
                    onClick={handleExportExcel}
                    disabled={!dataFiltered.length}
                  >
                    {t("Export")}
                  </ToolbarButton>
                </Tooltip>
                <ToolbarButton
                  data-testid="folder-metadata-overview-refresh"
                  type="primary"
                  faIcon={["fas", "sync"]}
                  onClick={handleRefresh}
                  loading={folderMetadataOverviewStore.loading}
                >
                  {t("Refresh")}
                </ToolbarButton>
                {hideModal ? <ModalCloseBtn onClick={hideModal} className="relative-position" /> : null}
              </Space>
            </Col>
          </Row>
        </div>
        <div className="folder-metadata-overview-table" data-testid="folder-metadata-overview-table">
          <ScrollableTable<FolderMetadataOverviewEntryWithKey>
            ref={tableRef}
            rowKey="_rowKey"
            data={tableData}
            columns={columns}
            loading={folderMetadataOverviewStore.loading}
            stretchHeight={true}
            sortBy={
              folderMetadataOverviewStore.sortBy === "value"
                ? "defaultwert"
                : (folderMetadataOverviewStore.sortBy as keyof FolderMetadataOverviewEntryWithKey)
            }
            sortDirection={folderMetadataOverviewStore.sortOrder as SortDirectionType}
            onSort={(sortBy, sortDirection) =>
              handleSort(sortBy as FolderMetadataSortBy, sortDirection as FolderMetadataSortOrder)
            }
            onFilter={handleFilter}
            filterDropdownMinWidth={250}
            onRowClick={({ rowData }) => goToFolder(rowData.folderId)}
            resizeableHeaders={COLUMN_WIDTHS_STORAGE_KEY}
          />
        </div>
      </div>
    </ConfigProvider>
  );
});

FolderMetadataOverviewPage.displayName = "FolderMetadataOverviewPage";
